// ===== React Select Styles Configuration File =====
// This file contains reusable styles for React Select components across the application
// Features: Default styles, dark theme, compact theme, and customizable styling options

// ===== Default React Select Styles =====
// Base styling configuration for all React Select components
export const reactSelectStyles = {
  // ===== Control Container Styling =====
  // Main container that wraps the select input and dropdown indicator
  control: (provided, state) => ({
    ...provided,
    minHeight: "38px",
    // height: '38px',
    fontSize: "1rem",
    borderColor: state.isFocused ? "var(--primary-color)" : "#ced4da", // Bootstrap primary and gray colors
    outline: state.isFocused ? "1px dashed var(--theme-color5)" : "none", // Dotted theme outline when focused
    outlineOffset: state.isFocused ? "3px" : "0px", // Add gap between outline and border
    boxShadow: "none", // Remove box-shadow entirely
    borderRadius: "0.375rem", // Rounded corners matching Bootstrap
    "&:hover": {
      borderColor: state.isFocused ? "var(--primary-color)" : "#ced4da", // Bootstrap primary on hover
    },
  }),

  // ===== Value Container Styling =====
  // Container that holds the selected value(s) and input field
  valueContainer: (provided) => ({
    ...provided,
    minHeight: "38px",
    padding: "0 8px", // Consistent padding
     overflowY: "auto",
  }),

  // ===== Input Field Styling =====
  // Styling for the text input field within the select
  input: (provided) => ({
    ...provided,
    margin: "0px",
    padding: "0px", // Remove default padding
    color: "#495057", // Bootstrap dark gray text color
  }),

  // ===== Indicators Container Styling =====
  // Container for dropdown arrow and clear button
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "38px", // Match control height
  }),

  // ===== Dropdown Indicator Styling =====
  // Styling for the dropdown arrow icon
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "var(--accent-color)" : "#6c757d", // Bootstrap colors
    padding: "0 8px", // Consistent padding
  }),

  // ===== Option Styling =====
  // Styling for individual options in the dropdown menu
  option: (provided, state) => ({
    ...provided,
    fontSize: "1rem",
    backgroundColor: state.isSelected
      ? "#f8f9fa" // Light gray for selected option
      : state.isFocused
        ? "#e9ecef" // Lighter gray for focused option
        : "white", // White for normal option
    color: "#495057", // Bootstrap dark gray text color
    cursor: "pointer", // Pointer cursor for clickable options
  }),

  // ===== Placeholder Styling =====
  // Styling for placeholder text
  placeholder: (provided) => ({
    ...provided,
    color: "#6c757d", // Bootstrap muted text color
  }),

  // ===== Single Value Styling =====
  // Styling for the selected value display
  singleValue: (provided) => ({
    ...provided,
    color: "#495057", // Bootstrap dark gray text color
  }),
};

// ===== Dark Theme React Select Styles =====
// Alternative dark theme styling for React Select components
export const reactSelectStylesDark = {
  ...reactSelectStyles, // Extend base styles

  // ===== Dark Theme Control Styling =====
  // Dark background and border colors for the main control
  control: (provided, state) => ({
    ...reactSelectStyles.control(provided, state),
    backgroundColor: "#343a40", // Bootstrap dark background
    border: state.isFocused
      ? "2px solid var(--accent-color)"
      : "1px solid #495057", // Bootstrap dark borders
    color: "white", // White text
    outline: state.isFocused ? "1px dotted var(--theme-color5)" : "none", // Dotted theme outline when focused
    outlineOffset: state.isFocused ? "3px" : "0px", // Add gap between outline and border
    boxShadow: "none", // Remove box-shadow entirely
  }),

  // ===== Dark Theme Menu Styling =====
  // Dark background for the dropdown menu
  menu: (provided, state) => ({
    ...provided,
    zIndex: 4, // Ensure menu appears above other elements
    backgroundColor: "#343a40", // Bootstrap dark menu background
    border: "1px solid #495057", // Bootstrap dark border
  }),

  // ===== Dark Theme Option Styling =====
  // Dark theme styling for dropdown options
  option: (provided, state) => ({
    ...reactSelectStyles.option(provided, state),
    backgroundColor: state.isSelected
      ? "var(--accent-color)" // Bootstrap primary for selected option
      : state.isFocused
        ? "rgba(var(--primary-rgb), 1)" // Bootstrap dark gray for focused option
        : "transparent", // Transparent for normal option
    color: "white", // White text for all states
    "&:hover": {
      backgroundColor: state.isSelected ? "var(--accent-color)" : "#495057", // Hover effects
    },
  }),

  // ===== Dark Theme Placeholder Styling =====
  // Light gray placeholder text for dark theme
  placeholder: (provided, state) => ({
    ...provided,
    color: "#adb5bd", // Bootstrap light gray placeholder
  }),

  // ===== Dark Theme Single Value Styling =====
  // White text for selected values in dark theme
  singleValue: (provided, state) => ({
    ...provided,
    color: "white", // White text
  }),

  // ===== Dark Theme Input Styling =====
  // White text for input field in dark theme
  input: (provided, state) => ({
    ...provided,
    margin: "0px",
    padding: "0px", // Remove default padding
    color: "white", // White text
  }),
};

// ===== Compact React Select Styles =====
// Smaller, more compact styling for space-constrained components
export const reactSelectStylesCompact = {
  ...reactSelectStyles, // Extend base styles

  // ===== Compact Control Styling =====
  // Reduced height and padding for compact appearance
  control: (provided, state) => ({
    ...reactSelectStyles.control(provided, state),
    minHeight: "32px", // Smaller height
    paddingTop: "0px !important", // Remove top padding
    paddingBottom: "0px !important", // Remove bottom padding
    outline: state.isFocused ? "1px dashed var(--theme-color5)" : "none", // Dotted theme outline when focused
    outlineOffset: state.isFocused ? "3px" : "0px", // Add gap between outline and border
    boxShadow: "none", // Remove box-shadow entirely
  }),

  // ===== Compact Option Styling =====
  // Reduced padding and font size for options
  option: (provided, state) => ({
    ...provided,
    fontSize: "14px", // Smaller font size
    backgroundColor: state.isSelected
      ? "#f8f9fa" // Light gray for selected option
      : state.isFocused
        ? "#e9ecef" // Lighter gray for focused option
        : "white", // White for normal option
    color: "#495057", // Bootstrap dark gray text color
    cursor: "pointer", // Pointer cursor for clickable options
    padding: "6px 12px", // Smaller padding
  }),

  // ===== Compact Placeholder Styling =====
  // Smaller font size for placeholder text
  placeholder: (provided, state) => ({
    ...provided,
    color: "#6c757d", // Bootstrap muted text color
    fontSize: "14px", // Smaller font size
  }),

  // ===== Compact Single Value Styling =====
  // Smaller font size for selected values
  singleValue: (provided, state) => ({
    ...provided,
    color: "#495057", // Bootstrap dark gray text color
    fontSize: "14px", // Smaller font size
  }),

  // ===== Compact Input Styling =====
  // Smaller font size for input text
  input: (provided, state) => ({
    ...provided,
    margin: "0px",
    padding: "0px", // Remove default padding
    color: "#495057", // Bootstrap dark gray text color
    fontSize: "14px", // Smaller font size
  }),
};

// ===== Default Export =====
// Export the default styles as the main export
export default reactSelectStyles;
